export function isUsingEmulators(): boolean {
  const envFlag = process.env.REACT_APP_USE_FIREBASE_EMULATORS;
  if (envFlag === 'true') return true;
  if (envFlag === 'false') return false;

  // If not explicitly configured, infer emulator usage when running on localhost
  // or on a private LAN IP (common for remote emulator hosts).
  if (typeof window === 'undefined') return false;

  const host = window.location.hostname;
  if (!host) return false;

  if (isLocalHostname(host)) return true;

  // RFC1918 private ranges: 10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16
  if (/^10\./.test(host)) return true;
  if (/^192\.168\./.test(host)) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(host)) return true;

  return false;
}

function isLocalHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';
}

/**
 * Resolve the emulator host for browser clients.
 *
 * Why: When the frontend is served from a remote emulator host (e.g. http://192.168.0.42:5000),
 * using "localhost" would point to the user's own machine and break signup/auth.
 */
export function getEmulatorHost(): string {
  const normalizeHost = (host?: string) => (host === '0.0.0.0' ? 'localhost' : host);

  const envHost = normalizeHost(process.env.REACT_APP_FIREBASE_EMULATOR_HOST);
  const browserHost = normalizeHost(typeof window !== 'undefined' ? window.location.hostname : undefined);

  // Escape hatch: explicitly force env host selection.
  if (process.env.REACT_APP_EMULATOR_HOST_STRICT === 'true') {
    return envHost || browserHost || 'localhost';
  }

  // If envHost and browserHost disagree on local vs remote, prefer the browserHost.
  // This keeps localhost dev/test stable even if the build-time env points to a LAN host.
  if (envHost && browserHost) {
    const envIsLocal = isLocalHostname(envHost);
    const browserIsLocal = isLocalHostname(browserHost);
    if (envIsLocal !== browserIsLocal) {
      return browserHost;
    }
  }

  // If an explicit, non-local env host is provided, honor it (unless overridden above).
  if (envHost && !isLocalHostname(envHost)) {
    return envHost;
  }

  // If we're in a browser and served from a non-local hostname, prefer that.
  if (browserHost && !isLocalHostname(browserHost)) {
    return browserHost;
  }

  // Otherwise, fall back to envHost (localhost/0.0.0.0) or localhost.
  return envHost || 'localhost';
}
